import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { PartCategoryCost } from '../../types';

interface PartCategoryCostBreakdownProps {
  cost: PartCategoryCost;
  /** Tints the toggle label green when the job is complete, amber otherwise. */
  completed?: boolean;
}

// One list so every header gets the sticky classes — a column added by hand
// without them scrolls away with the body.
const HEADERS: ReadonlyArray<[label: string, align: 'text-left' | 'text-right']> = [
  ['Chunk', 'text-left'],
  ['Parts', 'text-right'],
  ['Input tok.', 'text-right'],
  ['Output tok.', 'text-right'],
  ['USD', 'text-right'],
  ['SGD', 'text-right'],
];

/**
 * Part-category cost breakdown (extracted from JobDetail).
 *
 * Every numeric field is coalesced with `?? 0` and the chunk list with `?? []`
 * because a resumed/partway job — or one whose cost blob predates a field — can
 * omit any of them at runtime, even though the TypeScript type once declared
 * them required. Calling `.toFixed()` / `.toLocaleString()` on a missing value
 * threw and crashed the whole Job Detail page via the ErrorBoundary. Mirror of
 * the guard applied to DeduplicationStatsPanel.
 */
export const PartCategoryCostBreakdown: React.FC<PartCategoryCostBreakdownProps> = ({
  cost,
  completed,
}) => {
  const [open, setOpen] = useState(false);
  const chunks = cost.chunk_costs ?? [];

  return (
    <div className="mt-3">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 text-xs font-medium ${
          completed
            ? 'text-qc-success'
            : 'text-qc-warning'
        }`}
        data-testid="toggle-cost-breakdown-btn"
      >
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {open ? 'Hide' : 'View'} Cost Breakdown &mdash; ${(cost.total_cost_usd ?? 0).toFixed(4)} USD / S${(cost.total_cost_sgd ?? 0).toFixed(4)} SGD
      </button>
      {open && (
        // A completed 22k-part run carries hundreds of chunk rows; cap the
        // height so the table scrolls inside the card instead of running off
        // the page, and pin the header so the columns stay readable.
        <div
          className="mt-2 max-h-80 overflow-y-auto overflow-x-auto border border-border rounded"
          data-testid="cost-breakdown-scroll"
        >
          <table className="w-full text-xs border-collapse" data-testid="cost-breakdown-table">
            <thead>
              <tr className="bg-bg-subtle text-text-secondary">
                {HEADERS.map(([label, align]) => (
                  <th key={label} className={`sticky top-0 z-10 bg-bg-subtle ${align} px-2 py-1.5 font-medium`}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chunks.map((c) => (
                <tr key={c.chunk_index} className="border-t border-border">
                  <td className="px-2 py-1">{(c.chunk_index ?? 0) + 1}</td>
                  <td className="px-2 py-1 text-right">{c.parts_in_chunk ?? 0}</td>
                  <td className="px-2 py-1 text-right">{(c.prompt_tokens ?? 0).toLocaleString()}</td>
                  <td className="px-2 py-1 text-right">{(c.completion_tokens ?? 0).toLocaleString()}</td>
                  <td className="px-2 py-1 text-right">${(c.cost_usd ?? 0).toFixed(5)}</td>
                  <td className="px-2 py-1 text-right">S${(c.cost_sgd ?? 0).toFixed(5)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border font-semibold">
                <td className="px-2 py-1" colSpan={2}>Total</td>
                <td className="px-2 py-1 text-right">{(cost.total_prompt_tokens ?? 0).toLocaleString()}</td>
                <td className="px-2 py-1 text-right">{(cost.total_completion_tokens ?? 0).toLocaleString()}</td>
                <td className="px-2 py-1 text-right">${(cost.total_cost_usd ?? 0).toFixed(4)}</td>
                <td className="px-2 py-1 text-right">S${(cost.total_cost_sgd ?? 0).toFixed(4)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
